import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enum constants ────────────────────────────────────────────────────────
export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  OPEN: 'OPEN',
  PAID: 'PAID',
  VOID: 'VOID',
  OVERDUE: 'OVERDUE',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const PaymentMethod = {
  VA_BCA: 'VA_BCA',
  VA_BNI: 'VA_BNI',
  VA_BRI: 'VA_BRI',
  VA_MANDIRI: 'VA_MANDIRI',
  VA_PERMATA: 'VA_PERMATA',
  QRIS: 'QRIS',
  GOPAY: 'GOPAY',
  MANUAL_TRANSFER: 'MANUAL_TRANSFER',
  CASH: 'CASH',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PaymentStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
  EXPIRED: 'EXPIRED',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

// Money in IDR cents — plain integer (SQLite/D1 INTEGER, JS number).
// Max safe = 2^53 cents = Rp 90T. We're way under.
const moneyCentsInt = z.number().int().nonnegative();

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

// ─── Monthly fee config (one fee per cluster + per-unit overrides) ──────────
const monthStr = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');

// PUT /admin/billing/fee — set/replace the cluster's single monthly fee.
export const setMonthlyFeeRequest = z.object({
  amount_cents: z.number().int().positive(),
  start_month: monthStr, // first month billing runs, e.g. "2026-07"
  due_day: z.number().int().min(1).max(28),
});
export type SetMonthlyFeeRequest = z.infer<typeof setMonthlyFeeRequest>;

// PUT /admin/billing/fee/override — set or clear one unit's override.
// override_cents: null clears (unit reverts to the cluster fee); 0 = exempt.
export const setUnitFeeOverrideRequest = z.object({
  unit_id: uuid,
  override_cents: z.number().int().min(0).nullable(),
});
export type SetUnitFeeOverrideRequest = z.infer<typeof setUnitFeeOverrideRequest>;

export const unitFeeOverride = z.object({
  unit_id: uuid,
  label: z.string(),
  override_cents: z.number().int().min(0),
});
export type UnitFeeOverride = z.infer<typeof unitFeeOverride>;

// GET /admin/billing/fee
export const monthlyFeeConfig = z.object({
  fee: z
    .object({
      amount_cents: moneyCentsInt,
      start_month: monthStr,
      due_day: z.number().int().min(1).max(28),
      is_active: z.boolean(),
    })
    .nullable(),
  overrides: z.array(unitFeeOverride),
});
export type MonthlyFeeConfig = z.infer<typeof monthlyFeeConfig>;

// ─── Public shapes ────────────────────────────────────────────────────────
export const publicInvoiceItem = z.object({
  id: uuid,
  description: z.string(),
  quantity: z.number().int().min(1),
  unit_price_cents: moneyCentsInt,
  total_cents: moneyCentsInt,
});
export type PublicInvoiceItem = z.infer<typeof publicInvoiceItem>;

export const publicInvoice = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  number: z.string(),
  status: z.enum([
    InvoiceStatus.DRAFT,
    InvoiceStatus.OPEN,
    InvoiceStatus.PAID,
    InvoiceStatus.VOID,
    InvoiceStatus.OVERDUE,
  ]),
  issue_date: dateStr,
  due_date: dateStr,
  subtotal_cents: moneyCentsInt,
  tax_cents: moneyCentsInt,
  total_cents: moneyCentsInt,
  paid_cents: moneyCentsInt,
  notes: z.string().nullable(),
  source: z.enum(['MANUAL', 'FEE_SCHEDULE', 'BOOKING']),
  paid_at: isoDatetime.nullable(),
  voided_at: isoDatetime.nullable(),
  voided_reason: z.string().nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
  items: z.array(publicInvoiceItem).optional(),
  // Lite version for list responses (items omitted)
});
export type PublicInvoice = z.infer<typeof publicInvoice>;

export const publicPayment = z.object({
  id: uuid,
  invoice_id: uuid,
  amount_cents: moneyCentsInt,
  method: z.enum([
    PaymentMethod.VA_BCA,
    PaymentMethod.VA_BNI,
    PaymentMethod.VA_BRI,
    PaymentMethod.VA_MANDIRI,
    PaymentMethod.VA_PERMATA,
    PaymentMethod.QRIS,
    PaymentMethod.GOPAY,
    PaymentMethod.MANUAL_TRANSFER,
    PaymentMethod.CASH,
  ]),
  provider: z.enum(['MIDTRANS', 'MANUAL']),
  status: z.enum([
    PaymentStatus.PENDING,
    PaymentStatus.PROCESSING,
    PaymentStatus.PAID,
    PaymentStatus.EXPIRED,
    PaymentStatus.FAILED,
    PaymentStatus.REFUNDED,
  ]),
  va_number: z.string().nullable(),
  qr_string: z.string().nullable(),
  deeplink_url: z.string().url().nullable(),
  expires_at: isoDatetime.nullable(),
  paid_at: isoDatetime.nullable(),
  receipt_url: z.string().url().nullable(),
  created_at: isoDatetime,
});
export type PublicPayment = z.infer<typeof publicPayment>;

// ─── Resident query (GET /invoices) ───────────────────────────────────────
export const listInvoicesQuery = paginationQuery.extend({
  status: z
    .enum([
      InvoiceStatus.DRAFT,
      InvoiceStatus.OPEN,
      InvoiceStatus.PAID,
      InvoiceStatus.VOID,
      InvoiceStatus.OVERDUE,
    ])
    .optional(),
  since: dateStr.optional(),
});
export type ListInvoicesQuery = z.infer<typeof listInvoicesQuery>;

// ─── Staff (GET /admin/invoices) ──────────────────────────────────────────
export const adminListInvoicesQuery = listInvoicesQuery.extend({
  unit_id: uuid.optional(),
});
export type AdminListInvoicesQuery = z.infer<typeof adminListInvoicesQuery>;

// ─── Staff: create manual invoice ─────────────────────────────────────────
export const createInvoiceRequest = z.object({
  unit_id: uuid,
  description: z.string().min(1).max(200).trim(),
  amount_cents: moneyCentsInt.min(1),
  due_date: dateStr,
  issue_date: dateStr.optional(),
  tax_cents: moneyCentsInt.optional(),
  notes: z.string().max(500).optional(),
});
export type CreateInvoiceRequest = z.infer<typeof createInvoiceRequest>;

// ─── Staff: void ──────────────────────────────────────────────────────────
export const voidInvoiceRequest = z.object({
  reason: z.string().min(1).max(200).trim(),
});

// ─── Staff: mark paid manually ────────────────────────────────────────────
export const markPaidRequest = z.object({
  method: z.enum([PaymentMethod.MANUAL_TRANSFER, PaymentMethod.CASH]),
  amount_cents: moneyCentsInt.min(1),
  paid_at: isoDatetime.optional(),
  proof_url: z.string().url().optional(),
  note: z.string().max(500).optional(),
});
export type MarkPaidRequest = z.infer<typeof markPaidRequest>;

// ─── Resident: pay via Midtrans (POST /invoices/:id/pay) ───────────────────
// VA_MANDIRI is deliberately absent: Midtrans handles Mandiri via `echannel`
// (bill key + biller code), which doesn't fit the va_number column. Deferred.
// GOPAY is the direct-deeplink e-wallet path (va_number for VA_*, qr_string for
// QRIS, deeplink_url for GOPAY).
export const payInvoiceRequest = z.object({
  method: z.enum([
    PaymentMethod.VA_BCA,
    PaymentMethod.VA_BNI,
    PaymentMethod.VA_BRI,
    PaymentMethod.VA_PERMATA,
    PaymentMethod.QRIS,
    PaymentMethod.GOPAY,
  ]),
});
export type PayInvoiceRequest = z.infer<typeof payInvoiceRequest>;
